// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';

type Props = {|
  /** Uses a transparent background, useful for when placed on a dark background */
  inverted?: boolean,

  /** Whether to center the tabs or not */
  centered?: boolean,

  /** Tab text size. Defaults to medium */
  size?: 'small' | 'medium' | 'large',

  /** Custom classes to apply to the tabs container */
  className?: string,

  /** Contents of your tabs container. Recommended to use <Tab>. */
  children: React.Node,
|};

/** Tabs container styleguide component. Used best with <Tab> */
const NavTabs = (props: Props) => {
  const className = classnames('ui_tabs', props.className, {
    inverted: props.inverted,
    centered: props.centered,
    small: props.size === 'small',
    medium: props.size === 'medium',
    large: props.size === 'large',
  });

  return <div className={className}>{props.children}</div>;
};

NavTabs.defaultProps = {
  inverted: false,
  centered: false,
  size: 'medium',
  className: undefined,
};

type TabProps = {|
  /** Indicate that this is the active tab. */
  active?: boolean,

  /** Tag name, <span> by default */
  tagName: string,

  /** Tab name data attribute - optional, adds a data-tab-name attribute to your NavTab element
   * which allows tabs to toggle in and out of the 'active' state without changing the tab's
   * dimensions.  Should usually be the same localized string value that 'props.children' renders.
   * ex: this.props.children === <span>Foo Bar</span>: this.props.tabNameAttr = "Foo Bar" */
  tabNameAttr: ?string,

  /** Calback to run when tab is clicked */
  onClick?: () => void,

  /** Contents of your tab. */
  children: React.Node,
|};

/** A tab to drop into <NavTabs>. Defined in the same file because options NavTabs has affects the styling of NavTab. */
const NavTab = (props: TabProps) => {
  const className = classnames('ui_tab', {
    active: props.active,
  });

  return React.createElement(
    props.tagName,
    {
      className,
      'data-tab-name': props.tabNameAttr,
      onClick: props.onClick,
    },
    props.children,
  );
};

NavTab.defaultProps = {
  active: false,
  tagName: 'span',
  tabNameAttr: null,
};

// @deprecated: Use @ta/core-ui.nav-tabs
export { NavTab };
// @deprecated: Use @ta/core-ui.nav-tabs
export default NavTabs;
